// Mini App "Overview" page. Owns data fetching (TanStack Query hooks from
// entities/community) and composes the widgets/overview-grid. Only the
// active-community Zustand store is shared across pages; every other piece
// of state is server-state owned by TanStack Query.
//
// Per the FSD layer graph, pages may import from widgets, features,
// entities, and shared. They may NOT import from another page.

import { useEffect } from "react";

import {
  CommunitySelector,
  selectActiveCommunityId,
  useActiveCommunityStore,
  useCommunitiesQuery,
  useHealthQuery,
  useOverviewQuery,
} from "@entities/community";
import {
  OVERVIEW_STRINGS,
  OverviewEmptyState,
  OverviewErrorState,
  OverviewGrid,
  OverviewSkeleton,
} from "@widgets/overview-grid";

export function OverviewPage() {
  const activeCommunityId = useActiveCommunityStore(selectActiveCommunityId);
  const setActiveCommunityId = useActiveCommunityStore(
    (s) => s.setActiveCommunityId,
  );

  const communitiesQuery = useCommunitiesQuery();

  // Auto-select the first community as soon as the list resolves and the
  // store hasn't been seeded yet. This keeps the URL parameter-free for
  // the MVP — the selector lets the user switch.
  useEffect(() => {
    if (activeCommunityId !== null) return;
    const first = communitiesQuery.data?.[0];
    if (first) setActiveCommunityId(first.id);
  }, [activeCommunityId, communitiesQuery.data, setActiveCommunityId]);

  const overviewQuery = useOverviewQuery(activeCommunityId);
  const healthQuery = useHealthQuery(activeCommunityId);

  // Header is always rendered so the selector + bell don't pop in late.
  const header = (
    <header className="flex items-center justify-between gap-3 pb-2">
      <h1 className="text-lg font-semibold">{OVERVIEW_STRINGS.pageTitle}</h1>
      <div className="flex items-center gap-2">
        {communitiesQuery.data && communitiesQuery.data.length > 0 ? (
          <CommunitySelector
            communities={communitiesQuery.data}
            activeCommunityId={activeCommunityId}
            onSelect={setActiveCommunityId}
            label={OVERVIEW_STRINGS.selectorLabel}
          />
        ) : null}
        <button
          type="button"
          aria-label={OVERVIEW_STRINGS.notificationsAria}
          className="flex h-11 w-11 items-center justify-center rounded-md border border-input bg-background text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <BellIcon />
        </button>
      </div>
    </header>
  );

  // 1) communities list errored — page-wide error state.
  if (communitiesQuery.isError) {
    return (
      <main className="container flex min-h-screen flex-col gap-4 py-4">
        {header}
        <OverviewErrorState
          title={OVERVIEW_STRINGS.errorTitle}
          body={
            communitiesQuery.error?.message ??
            OVERVIEW_STRINGS.errorBodyDefault
          }
          retryLabel={OVERVIEW_STRINGS.retry}
          onRetry={() => void communitiesQuery.refetch()}
        />
      </main>
    );
  }

  // 2) communities list resolved → user has zero communities → onboarding.
  if (communitiesQuery.data && communitiesQuery.data.length === 0) {
    return (
      <main className="container flex min-h-screen flex-col gap-4 py-4">
        {header}
        <OverviewEmptyState
          title={OVERVIEW_STRINGS.noCommunity.title}
          body={OVERVIEW_STRINGS.noCommunity.body}
        />
      </main>
    );
  }

  // 3) Either or both detail queries errored — surface a single retry CTA.
  if (overviewQuery.isError || healthQuery.isError) {
    const message =
      overviewQuery.error?.message ??
      healthQuery.error?.message ??
      OVERVIEW_STRINGS.errorBodyDefault;
    return (
      <main className="container flex min-h-screen flex-col gap-4 py-4">
        {header}
        <OverviewErrorState
          title={OVERVIEW_STRINGS.errorTitle}
          body={message}
          retryLabel={OVERVIEW_STRINGS.retry}
          onRetry={() => {
            void overviewQuery.refetch();
            void healthQuery.refetch();
          }}
        />
      </main>
    );
  }

  // 4) Loading — render the skeleton beneath the header so layout doesn't
  // shift when the data lands.
  if (
    !communitiesQuery.data ||
    !overviewQuery.data ||
    !healthQuery.data ||
    activeCommunityId === null
  ) {
    return (
      <main className="container flex min-h-screen flex-col gap-4 py-4">
        {header}
        <OverviewSkeleton loadingLabel={OVERVIEW_STRINGS.loading} />
      </main>
    );
  }

  // 5) Happy path.
  return (
    <main className="container flex min-h-screen flex-col gap-4 py-4">
      {header}
      <OverviewGrid
        overview={overviewQuery.data}
        health={healthQuery.data}
      />
    </main>
  );
}

function BellIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}
